import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { officesAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Buildings, Plus, PencilSimple, Trash, Users, UserCircle, ArrowRight, MapPin, Phone, Envelope, GitMerge
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SuperAdminPage = () => {
  const { user, checkAuth } = useAuth();
  const [offices, setOffices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '', email: '' });
  // Merge office state
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergeConfirmText, setMergeConfirmText] = useState('');
  const [merging, setMerging] = useState(false);

  const openMerge = (office) => {
    setMergeSource(office);
    setMergeTargetId('');
    setMergeConfirmText('');
  };

  const handleMerge = async () => {
    if (!mergeSource || !mergeTargetId) { toast.error('Pick a destination office'); return; }
    if (mergeConfirmText !== 'MERGE') { toast.error('Type MERGE to confirm'); return; }
    setMerging(true);
    try {
      const res = await officesAPI.mergeInto(mergeSource.id, mergeTargetId);
      const m = res.data?.moved || {};
      toast.success(
        `Merged. Moved: ${m.client_profiles || 0} clients, ${m.caregiver_profiles || 0} caregivers, ${m.visits || 0} visits, ${m.users || 0} users.`
      );
      setMergeSource(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally { setMerging(false); }
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [officesRes, statsRes] = await Promise.all([
        officesAPI.getAll(),
        officesAPI.getStats()
      ]);
      setOffices(officesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading offices:', error);
      toast.error('Failed to load offices');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingOffice) {
        await officesAPI.update(editingOffice.id, formData);
        toast.success('Office updated');
      } else {
        await officesAPI.create(formData);
        toast.success('Office created');
      }
      setIsCreateOpen(false);
      setEditingOffice(null);
      setFormData({ name: '', address: '', phone: '', email: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to save office');
    }
  };

  const handleEdit = (office) => {
    setEditingOffice(office);
    setFormData({ name: office.name, address: office.address || '', phone: office.phone || '', email: office.email || '' });
    setIsCreateOpen(true);
  };

  const handleDelete = async (officeId) => {
    try {
      await officesAPI.delete(officeId);
      toast.success('Office deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete office');
    }
  };

  const handleSwitchToOffice = async (officeId) => {
    try {
      const res = await officesAPI.switch(officeId);
      toast.success(res.data.message);
      await checkAuth();
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to switch office');
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Buildings weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h3 className="font-heading text-lg font-medium">Access Restricted</h3>
        <p className="text-muted-foreground text-sm mt-1">Super Admin access required</p>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="super-admin-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">Office Management</h1>
          <p className="text-muted-foreground mt-1">Manage all offices across your organization</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) { setEditingOffice(null); setFormData({ name: '', address: '', phone: '', email: '' }); } setIsCreateOpen(open); }}>
          <DialogTrigger asChild>
            <Button className="rounded-xl gap-2" data-testid="create-office-btn">
              <Plus weight="bold" className="w-4 h-4" /> Create Office
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingOffice ? 'Edit Office' : 'Create New Office'}</DialogTitle>
              <DialogDescription>Set up a new office location</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Office Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Springfield Office" required className="rounded-xl" data-testid="office-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main St, Springfield, IL 62701" className="rounded-xl" data-testid="office-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 555-0000" className="rounded-xl" data-testid="office-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="office@caregrid.com" className="rounded-xl" data-testid="office-email-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 rounded-xl" data-testid="save-office-btn">{editingOffice ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Global Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Offices</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{stats.total_offices}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Buildings weight="duotone" className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Users</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{stats.total_users}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                  <Users weight="duotone" className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Clients</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{stats.total_clients}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Users weight="duotone" className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Caregivers</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{stats.total_caregivers}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <UserCircle weight="duotone" className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Offices Grid */}
      {offices.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Buildings weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No offices yet</h3>
            <p className="text-muted-foreground text-sm mt-1">Create your first office to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {offices.map(office => (
            <Card key={office.id} className="rounded-2xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-200" data-testid={`office-card-${office.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Buildings weight="duotone" className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-lg">{office.name}</CardTitle>
                      <CardDescription>Created {format(new Date(office.created_at), 'MMM d, yyyy')}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {office.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">{office.address}</span>
                  </div>
                )}
                {office.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone weight="duotone" className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{office.phone}</span>
                  </div>
                )}
                {office.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Envelope weight="duotone" className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{office.email}</span>
                  </div>
                )}

                <div className="flex gap-4 pt-2">
                  <div className="text-center">
                    <p className="font-heading text-xl font-semibold">{office.user_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Users</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-xl font-semibold">{office.client_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Clients</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-xl font-semibold">{office.caregiver_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Caregivers</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button className="flex-1 rounded-xl gap-1" onClick={() => handleSwitchToOffice(office.id)} data-testid={`switch-office-${office.id}`}>
                    <ArrowRight weight="bold" className="w-4 h-4" /> Enter Office
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => handleEdit(office)} data-testid={`edit-office-${office.id}`} title="Edit office">
                    <PencilSimple weight="bold" className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openMerge(office)} data-testid={`merge-office-${office.id}`} title="Merge into another office">
                    <GitMerge weight="bold" className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-xl text-destructive" onClick={() => handleDelete(office.id)} data-testid={`delete-office-${office.id}`} title="Delete office">
                    <Trash weight="bold" className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Merge Office Dialog */}
      <Dialog open={!!mergeSource} onOpenChange={(o) => { if (!o && !merging) setMergeSource(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="merge-office-dialog">
          {mergeSource && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Merge Office</DialogTitle>
                <DialogDescription>
                  Move all data from <strong>{mergeSource.name}</strong> into another office. Holidays are unioned; the source office stays in the system but is marked inactive.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="p-3 rounded-xl bg-muted/40 text-sm space-y-1">
                  <p><span className="text-muted-foreground">Source:</span> <span className="font-medium">{mergeSource.name}</span></p>
                  <p className="text-xs text-muted-foreground">{mergeSource.client_count || 0} clients · {mergeSource.caregiver_count || 0} caregivers · {mergeSource.user_count || 0} users</p>
                </div>
                <div className="space-y-1">
                  <Label>Destination office</Label>
                  <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                    <SelectTrigger className="rounded-xl" data-testid="merge-target-select">
                      <SelectValue placeholder="Pick destination office..." />
                    </SelectTrigger>
                    <SelectContent>
                      {offices.filter(o => o.id !== mergeSource.id).map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Type <span className="font-mono">MERGE</span> to confirm</Label>
                  <Input
                    value={mergeConfirmText}
                    onChange={(e) => setMergeConfirmText(e.target.value.toUpperCase())}
                    placeholder="MERGE"
                    className="rounded-xl font-mono"
                    data-testid="merge-confirm-text"
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setMergeSource(null)} disabled={merging}>Cancel</Button>
                  <Button
                    className="flex-1 rounded-xl gap-2 bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={handleMerge}
                    disabled={merging || mergeConfirmText !== 'MERGE' || !mergeTargetId}
                    data-testid="confirm-merge-btn"
                  >
                    <GitMerge weight="bold" className="w-4 h-4" />
                    {merging ? 'Merging...' : 'Merge'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
